/**
 * EvoProductMergeDefaultsModal Component
 *
 * A wrapper component that renders the ProductMergeDefaultsModal with EVO Design System
 * styling using Tailwind CSS scoped to the EvoWrapper component.
 *
 * This component wraps the modal in EvoWrapper for proper Tailwind styling
 * and integrates with the actual CRUD hooks for data operations.
 */

import React from 'react';

import { EvoWrapper } from '@/components/evo/EvoWrapper';
import { ProductMergeDefaultsModal } from './ProductMergeDefaultsModal';
import { ProductMergeDefault, MergeType, UserNameMap } from './types';
import useGetProductMergeDefaults from './hooks/useGetProductMergeDefaults';
import useAddProductMergeDefault from './hooks/useAddProductMergeDefault';
import useUpdateProductMergeDefault from './hooks/useUpdateProductMergeDefault';
import useDeleteProductMergeDefault from './hooks/useDeleteProductMergeDefault';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';

interface EvoProductMergeDefaultsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refreshGrid?: () => void;
}

export function EvoProductMergeDefaultsModal({
  open,
  onOpenChange,
  refreshGrid,
}: EvoProductMergeDefaultsModalProps) {
  const {
    getProductMergeDefaults,
    productMergeDefaults,
    isLoadingProductMergeDefaults,
  } = useGetProductMergeDefaults();

  const { addMergeDefault, isLoadingAddMergeDefault } = useAddProductMergeDefault();
  const { updateMergeDefault, isLoadingUpdateMergeDefault } = useUpdateProductMergeDefault();
  const { deleteMergeDefault, isLoadingDeleteMergeDefault } = useDeleteProductMergeDefault();
  const { searchUsers, isLoadingSearchUsers } = useSearchUsers();

  const [userNameMap, setUserNameMap] = React.useState<UserNameMap>({});

  React.useEffect(() => {
    if (open) {
      getProductMergeDefaults();
    }
  }, [open]);

  React.useEffect(() => {
    if (!isLoadingProductMergeDefaults && productMergeDefaults?.length) {
      const getUsers = async () => {
        const userIds = productMergeDefaults
          .flatMap((item) => [item.createdBy, item.updatedBy])
          .filter((id): id is string => typeof id === 'string' && !!id);
        const uniqueUserIds = [...new Set(userIds)];

        if (uniqueUserIds.length > 0) {
          const users = await searchUsers({ ids: uniqueUserIds });
          const nameMap: UserNameMap = {};
          users.forEach((user) => {
            nameMap[user.Id] = `${user.FirstName} ${user.LastName}`.trim() || user.EmailAddress;
          });
          setUserNameMap(nameMap);
        }
      };

      getUsers();
    }
  }, [productMergeDefaults, isLoadingProductMergeDefaults, searchUsers]);

  const transformedData: ProductMergeDefault[] = React.useMemo(() => {
    if (!productMergeDefaults) return [];

    return productMergeDefaults.map((item) => ({
      id: item.iD,
      stockCodeContains: item.stockCodeContains,
      mergeType: item.mergeType ?? null,
      createdBy: item.createdBy ?? null,
      updatedBy: item.updatedBy ?? null,
      created: item.created ?? null,
      updated: item.updated ?? null,
    }));
  }, [productMergeDefaults]);

  const handleAdd = (stockCodeContains: string, mergeType: MergeType) => {
    addMergeDefault(
      {
        input: {
          stockCodeContains,
          mergeType,
        },
      },
      () => {
        getProductMergeDefaults();
        refreshGrid?.();
      },
    );
  };

  const handleEdit = (item: ProductMergeDefault, stockCodeContains: string, mergeType: MergeType) => {
    updateMergeDefault(
      {
        input: {
          id: item.id,
          stockCodeContains,
          mergeType,
        },
      },
      () => {
        getProductMergeDefaults();
        refreshGrid?.();
      },
    );
  };

  const handleDelete = (item: ProductMergeDefault) => {
    deleteMergeDefault(item.id, () => {
      getProductMergeDefaults();
      refreshGrid?.();
    });
  };

  const isLoading =
    isLoadingProductMergeDefaults ||
    isLoadingAddMergeDefault ||
    isLoadingUpdateMergeDefault ||
    isLoadingDeleteMergeDefault ||
    isLoadingSearchUsers;

  if (!open) {
    return null;
  }

  return (
    <EvoWrapper>
      <ProductMergeDefaultsModal
        open={open}
        onOpenChange={onOpenChange}
        data={transformedData}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={isLoading}
        userNameMap={userNameMap}
      />
    </EvoWrapper>
  );
}

export default EvoProductMergeDefaultsModal;
